import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { ApolloError } from '@apollo/client'
import { auth } from '../../lib/auth'
import { parseYmd, toYmd } from '../../lib/format'

// Shared building blocks for the report pages under src/pages/reports/.
// This file is the single home for the date helpers and the small UI pieces the
// report pages have in common — they used to be module-locals of ReportsPage.tsx,
// which no longer exists.

export const REST_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000/graphql').replace('/graphql', '')

export const today = () => new Date().toISOString().slice(0, 10)

export const firstOfMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export const lastMonth = (): [string, string] => {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const last = new Date(y, d.getMonth() + 1, 0).getDate()
  return [`${y}-${m}-01`, `${y}-${m}-${String(last).padStart(2, '0')}`]
}

export const formatMethod = (method: string) =>
  method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** iso_day (1=Mon … 7=Sun) → label via DAY_LABELS[iso_day - 1]. */
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ── Month machinery (calendar-month report pages) ────────────────────────────

// parseYmd / toYmd moved to lib/format.ts (2026-10-02) so lib/deliveryHistory.ts can use them
// without a lib → pages dependency; re-exported here so the report pages keep one import.
export { parseYmd, toYmd }

/**
 * The current month as "YYYY-MM", from LOCAL getters. today() below is UTC
 * (toISOString) and returns yesterday between 00:00 and 02:00 CET — wrong for
 * anything deciding what is "in the future".
 */
export const currentLocalMonth = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export const monthLabel = (month: string): string => {
  const [y, m] = month.split('-').map(Number)
  // The reports section is English; the Dashboard's nl-NL formatting is a known divergence.
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

export const stepMonth = (month: string, delta: number): string => {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** 1 = Mon … 7 = Sun, matching the API's iso_day and DAY_LABELS. */
export const isoDayOf = (date: Date): number => ((date.getDay() + 6) % 7) + 1

/**
 * A complete, real "YYYY-MM-DD". Date inputs emit '' while being cleared or
 * keyboard-edited — feeding that into dateRangeToUtc throws mid-render, so
 * every free-form range must pass this gate before building filters.
 */
export const isValidYmd = (date: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(parseYmd(date).getTime())

/**
 * The n most recent COMPLETED ISO weeks (Mon–Sun): the Monday n weeks before the
 * current week's Monday through last Sunday. The running week is excluded on
 * purpose — a partial week would drag every weekday average down.
 */
export const lastNFullWeeks = (n: number): { start: string; end: string } => {
  const now = new Date()
  const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (isoDayOf(now) - 1))
  const end = new Date(thisMonday)
  end.setDate(end.getDate() - 1)
  const start = new Date(thisMonday)
  start.setDate(start.getDate() - n * 7)
  return { start: toYmd(start), end: toYmd(end) }
}

/** How often an iso weekday (1=Mon … 7=Sun) falls inside an inclusive YMD range. */
export const weekdayOccurrences = (startYmd: string, endYmd: string, isoDay: number): number => {
  const end = parseYmd(endYmd)
  let count = 0
  for (const d = parseYmd(startYmd); d <= end; d.setDate(d.getDate() + 1)) {
    if (isoDayOf(d) === isoDay) count++
  }
  return count
}

// ── Product Reports v2 shared pieces ─────────────────────────────────────────

/**
 * The Planning tab's fixed windows. The drill panel MUST use the same numbers:
 * its weekday sections issue the same document with the same variables, so a
 * drill opened from Planning is answered from the Apollo cache. Changing one
 * without the other silently breaks that.
 */
export const PLANNING_WEEKS = 8
export const TREND_WEEKS = 13

/** "Product · Variant", suppressing the variant when it just repeats the product name. */
export const labelOf = (productName: string, variantName: string) =>
  variantName && variantName !== productName ? `${productName} · ${variantName}` : productName

/** Compact average: whole units from 10 up, one decimal below. */
export const avgLabel = (value: number): string => (value >= 10 ? value.toFixed(0) : value.toFixed(1))

/**
 * PHASE-1 row key: the deployed rollups expose no variantId, so rows join on
 * productId + variantName (both read live from product/product_variant, so the
 * stats side and the catalog side can never disagree on the name). Phase 2
 * (byProduct) keys on variantId — every consumer greps to this one symbol.
 */
export const productVariantKey = (productId: number, variantName: string) =>
  `${productId}|${variantName}`

/**
 * True when a GraphQL error means "this field/argument does not exist in the
 * deployed schema" — the not-yet-deployed phase-2 surface. Anything else (500,
 * expired session, permissions) must fall through to a generic error, not the
 * "waiting for an API deploy" copy. Validation failures arrive as graphQLErrors
 * or, via the HTTP link, as a 400 networkError carrying result.errors.
 */
export const isMissingFieldError = (error: ApolloError): boolean => {
  const netErrors =
    (error.networkError as { result?: { errors?: { message?: string; extensions?: { code?: string } }[] } } | null)
      ?.result?.errors ?? []
  return [...error.graphQLErrors, ...netErrors].some(
    (e) =>
      e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
      /Cannot query field|Unknown argument|Unknown type/.test(e.message ?? '')
  )
}

/** Table header cell with full literal Tailwind classes (the JIT compiler cannot see interpolated ones). */
export const TH = ({ right, children }: { right?: boolean; children: React.ReactNode }) => (
  <th
    className={`px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap ${
      right ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
)

/**
 * Heat-grid cell classes/style, scaled against the row max (per-row scaling: a
 * bestseller must not flatten every other row into the same pale shade).
 * Intensity needs an inline style — Tailwind cannot generate per-cell alphas.
 */
export const heatCellProps = (
  qty: number,
  rowMax: number
): { className: string; style?: { backgroundColor: string } } => {
  const base = 'h-8 rounded flex items-center justify-center text-[11px] font-medium'
  if (qty === 0) return { className: `${base} bg-gray-50` }
  const t = rowMax > 0 ? qty / rowMax : 0
  return {
    className: `${base} ${t > 0.55 ? 'text-white' : 'text-gray-800'}`,
    style: { backgroundColor: `rgba(202, 67, 48, ${0.08 + 0.92 * t})` },
  }
}

export const MonthBar = ({ month, onChange }: { month: string; onChange: (m: string) => void }) => (
  <div className="flex justify-center">
    <div className="inline-flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-3 py-2">
      <button
        type="button"
        onClick={() => onChange(stepMonth(month, -1))}
        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
        title="Previous month"
      >
        <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
      </button>
      <span className="text-sm font-semibold text-gray-900 w-36 text-center">{monthLabel(month)}</span>
      <button
        type="button"
        disabled={month === currentLocalMonth()}
        onClick={() => onChange(stepMonth(month, 1))}
        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
        title="Next month"
      >
        <ChevronRightIcon className="h-4 w-4 text-gray-600" />
      </button>
    </div>
  </div>
)

/** A clicked heatmap cell: an (iso_day, hour) cell or an (iso_day, daypart) cell. */
export type SlotSelection =
  | { kind: 'hour'; iso_day: number; hour: number }
  | { kind: 'daypart'; iso_day: number; daypart: string; label: string }

/**
 * Bearer-fetch a CSV endpoint and trigger a browser download. Throws on a
 * non-OK response — callers own their `downloading` state and error toast.
 * The filename is chosen client-side; the server's Content-Disposition is ignored.
 */
export const downloadReportCsv = async (path: string, filename: string): Promise<void> => {
  const response = await fetch(`${REST_BASE}${path}`, {
    headers: { Authorization: `Bearer ${auth.getToken()}` },
  })
  if (!response.ok) throw new Error(`Server returned ${response.status}`)
  saveBlob(filename, await response.blob())
}

/** Trigger a browser download of a blob via a synthetic anchor. */
const saveBlob = (filename: string, blob: Blob): void => {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}

/**
 * Client-side CSV download for tables the REST export cannot produce (the
 * server endpoints ignore order_source, and these tables carry client-computed
 * columns anyway). Excel-NL shape: UTF-8 BOM + semicolon delimiter so a
 * double-click opens it in columns instead of one string per row.
 */
export const downloadCsvFromRows = (
  filename: string,
  header: string[],
  rows: (string | number | null)[][]
): void => {
  const escape = (value: string | number | null): string => {
    if (value == null) return ''
    const text = String(value)
    return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const body = [header, ...rows].map((row) => row.map(escape).join(';')).join('\r\n')
  saveBlob(filename, new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' }))
}

/** The API's OrderSource enum; null = every channel (never a breakdown). */
export type OrderSource = 'POS' | 'WEBSHOP' | 'HUBRISE' | 'KIOSK'

const CHANNELS: { value: OrderSource | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'POS', label: 'Counter' },
  { value: 'KIOSK', label: 'Kiosk' },
  { value: 'WEBSHOP', label: 'Webshop' },
  { value: 'HUBRISE', label: 'Third-party' },
]

/** Single-select channel filter — feeds order_source into every document below it. */
export const ChannelFilterChips = ({
  value,
  onChange,
}: {
  value: OrderSource | null
  onChange: (v: OrderSource | null) => void
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide mr-1">Channel</span>
    {CHANNELS.map((channel) => (
      <button
        key={channel.value ?? 'all'}
        type="button"
        onClick={() => onChange(channel.value)}
        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
          value === channel.value
            ? 'bg-gray-900 text-white'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
      >
        {channel.label}
      </button>
    ))}
  </div>
)

export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">{children}</h2>
)

export const StatCard = ({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
  </div>
)

export const EmptyData = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2 text-sm text-gray-400">
    <svg className="w-8 h-8 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
    No data for this period
  </div>
)

export const TableLoading = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
  </div>
)

export const QueryError = ({ message }: { message: string }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
    {message}
  </div>
)

export const AccessDenied = ({
  message = "You don't have permission to view financial reports.",
}: {
  message?: string
}) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3 text-sm text-gray-400">
    <svg className="w-10 h-10 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
    <p className="font-medium text-gray-500">Access denied</p>
    <p>{message}</p>
  </div>
)

/** Date range card with From/To inputs and This month / Last month / This year presets. */
export const DateRangeBar = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
}: {
  startDate: string
  endDate: string
  onStartChange: (v: string) => void
  onEndChange: (v: string) => void
}) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4">
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
          From
        </label>
        <input
          type="date"
          value={startDate}
          max={endDate}
          onChange={(e) => onStartChange(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
          To
        </label>
        <input
          type="date"
          value={endDate}
          min={startDate}
          max={today()}
          onChange={(e) => onEndChange(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>
      <div className="flex gap-2 ml-1">
        <button
          onClick={() => { onStartChange(firstOfMonth()); onEndChange(today()) }}
          className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          This month
        </button>
        <button
          onClick={() => { const [s, e] = lastMonth(); onStartChange(s); onEndChange(e) }}
          className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          Last month
        </button>
        <button
          onClick={() => { onStartChange(`${new Date().getFullYear()}-01-01`); onEndChange(today()) }}
          className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          This year
        </button>
      </div>
    </div>
  </div>
)
